// src/DoctorListSeo.jsx
import React, { useEffect } from "react";

/**
 * Lightweight SEO Head manager (no deps).
 * - Sets <title>
 * - Sets meta description, robots
 * - Sets canonical
 * - Sets OpenGraph + Twitter
 * - Sets hreflang alternates (optional)
 * - Injects multiple JSON-LD scripts (array)
 */
export default function DoctorListSEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  robots = "index,follow",
  lang = "sq", // "sq" | "en"
  alternates = [], // [{ hrefLang:"sq", href:"https://..." }, { hrefLang:"en", href:"https://..." }, { hrefLang:"x-default", href:"https://..." }]
  jsonLd = [], // array of objects
}) {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const upsertMeta = (key, value, attr = "name") => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    const upsertLink = (rel, href, extraAttrs = {}) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      Object.entries(extraAttrs).forEach(([k, v]) => {
        if (v == null) return;
        el.setAttribute(k, String(v));
      });
    };

    const removeExistingJsonLd = () => {
      document.head
        .querySelectorAll('script[data-seo="jsonld"]')
        .forEach((n) => n.remove());
    };

    // ---- Title + basics
    if (title) document.title = title;
    if (lang) document.documentElement.setAttribute("lang", lang);

    upsertMeta("description", description || "");
    upsertMeta("robots", robots);

    // ---- Canonical
    upsertLink("canonical", canonical);

    // ---- OpenGraph
    upsertMeta("og:type", ogType, "property");
    upsertMeta("og:title", title || "", "property");
    upsertMeta("og:description", description || "", "property");
    upsertMeta("og:url", canonical || "", "property");
    if (ogImage) upsertMeta("og:image", absolutizeMaybe(canonical, ogImage), "property");

    // ---- Twitter
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title || "");
    upsertMeta("twitter:description", description || "");
    if (ogImage) upsertMeta("twitter:image", absolutizeMaybe(canonical, ogImage));

    // ---- Alternates (hreflang)
    // remove old hreflang created by us
    document.head.querySelectorAll('link[data-seo="hreflang"]').forEach((n) => n.remove());
    (alternates || []).forEach((a) => {
      if (!a?.href || !a?.hrefLang) return;
      const ln = document.createElement("link");
      ln.setAttribute("rel", "alternate");
      ln.setAttribute("hreflang", a.hrefLang);
      ln.setAttribute("href", a.href);
      ln.setAttribute("data-seo", "hreflang");
      document.head.appendChild(ln);
    });

    // ---- JSON-LD
    removeExistingJsonLd();
    (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
      .filter(Boolean)
      .forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo", "jsonld");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });

    // cleanup only JSON-LD + hreflang we inserted (don’t nuke your other head tags)
    return () => {
      document.head
        .querySelectorAll('script[data-seo="jsonld"]')
        .forEach((n) => n.remove());
      document.head
        .querySelectorAll('link[data-seo="hreflang"]')
        .forEach((n) => n.remove());
    };
  }, [title, description, canonical, ogImage, ogType, robots, lang, JSON.stringify(alternates), JSON.stringify(jsonLd)]);

  return null;
}

function absolutizeMaybe(canonical, maybePath) {
  try {
    // already absolute
    if (/^https?:\/\//i.test(maybePath)) return maybePath;
    // build from canonical origin
    const u = new URL(canonical || "https://mikdok.com");
    const origin = u.origin;
    const path = (maybePath || "").startsWith("/") ? maybePath : `/${maybePath}`;
    return origin + path;
  } catch {
    return maybePath;
  }
}
